import { invokeFunction } from '@/lib/supabase';
import { parseInvokeErrorBody } from '@/lib/invoke-error';

export type UnsupportedAccountReason = 'non_personal_domain' | 'missing_scopes';

/** Thrown when the connected Gmail mailbox is not a supported personal account. */
export class UnsupportedAccountError extends Error {
  readonly reason: UnsupportedAccountReason;
  readonly email?: string;
  constructor(reason: UnsupportedAccountReason, email?: string) {
    super(`Unsupported Gmail account (${reason})`);
    this.name = 'UnsupportedAccountError';
    this.reason = reason;
    this.email = email;
  }
}

/**
 * Thrown when the mailbox behind the Google token is not the account the user
 * is signed in to Yaruta with (`gmail-forwarding-setup` Gate 2.5, YAR-325).
 *
 * Distinct from `UnsupportedAccountError` because the recovery is the
 * opposite: an unsupported account has nothing to retry and the user must come
 * back as a different one, whereas a mismatch is fixed by re-running the
 * picker and choosing the account already named on screen. Signing them out
 * here would throw away a session that is perfectly good.
 */
export class AccountMismatchError extends Error {
  readonly signedInEmail?: string;
  readonly selectedEmail?: string;
  constructor(signedInEmail?: string, selectedEmail?: string) {
    super('Connected mailbox differs from the signed-in account');
    this.name = 'AccountMismatchError';
    this.signedInEmail = signedInEmail;
    this.selectedEmail = selectedEmail;
  }
}

/**
 * Stable refusal codes sent by `gmail-forwarding-setup` in `body.code`
 * (YAR-332). `UNKNOWN` covers any verdict without a code this client
 * recognises — an older server, or a rejection that was never coded.
 */
export type ForwardingSetupErrorCode =
  | 'NOT_HOUSEHOLD_MEMBER'
  | 'NOT_HOUSEHOLD_PRIMARY'
  | 'GMAIL_PROFILE_UNAVAILABLE'
  | 'UNKNOWN';

const KNOWN_SETUP_ERROR_CODES: ReadonlySet<string> = new Set<
  Exclude<ForwardingSetupErrorCode, 'UNKNOWN'>
>([
  'NOT_HOUSEHOLD_MEMBER',
  'NOT_HOUSEHOLD_PRIMARY',
  'GMAIL_PROFILE_UNAVAILABLE',
]);

function isKnownSetupErrorCode(
  value: unknown,
): value is Exclude<ForwardingSetupErrorCode, 'UNKNOWN'> {
  return typeof value === 'string' && KNOWN_SETUP_ERROR_CODES.has(value);
}

/**
 * Read the refusal code off an error body. Prefers `code`; falls back to
 * `error` when that already holds a code (the same slot UNSUPPORTED_ACCOUNT
 * and ACCOUNT_MISMATCH use).
 */
function setupErrorCodeOf(
  body: Record<string, unknown>,
): ForwardingSetupErrorCode {
  if (isKnownSetupErrorCode(body.code)) return body.code;
  if (isKnownSetupErrorCode(body.error)) return body.error;
  return 'UNKNOWN';
}

/**
 * Thrown when `gmail-forwarding-setup` refuses with a server verdict other
 * than UNSUPPORTED_ACCOUNT / ACCOUNT_MISMATCH — not a member, not the
 * household primary, or Gmail itself refused to identify the mailbox. `code`
 * says which, so the UI can give each its own recovery.
 *
 * This exists because every non-2xx that was not `UNSUPPORTED_ACCOUNT` used to
 * reach the UI as a bare `Error`, which the onboarding screen rendered as
 * "Couldn't connect — check your internet". Four unrelated server rejections
 * all read as a network fault, and the only offered action ("Try again") could
 * not resolve any of them.
 */
export class ForwardingSetupError extends Error {
  readonly code: ForwardingSetupErrorCode;
  constructor(code: ForwardingSetupErrorCode, message: string) {
    super(message);
    this.name = 'ForwardingSetupError';
    this.code = code;
  }
}

interface GmailForwardingResult {
  label_id: string | null;
  filters_created: number;
  history_id: string | null;
  watch_expiry: string | null;
  errors: string[];
}

export interface GmailFiltersResyncResult {
  ok: boolean;
  /** Set when ok=false. Caller should route the user through OAuth. */
  needs_oauth?: boolean;
  filters_created?: number;
  stale_deleted?: number;
  errors?: string[];
}

/**
 * Set up Gmail label, filters, and Watch subscription for a household.
 *
 * @param householdId - The household to configure.
 * @param accessToken - Google OAuth access token with gmail.settings.basic,
 *   gmail.labels, and gmail.readonly scopes.
 *   Must come from the live auth session (AuthContext.session.provider_token).
 *   Do NOT use supabase.auth.getSession() — stored sessions do not carry provider_token.
 * @param refreshToken - Google OAuth refresh token for Watch renewal.
 *   From AuthContext.session.provider_refresh_token.
 */
export async function setupGmailForwarding(
  householdId: string,
  accessToken: string,
  refreshToken?: string | null,
): Promise<GmailForwardingResult> {
  const { data, error } = await invokeFunction('gmail-forwarding-setup', {
    body: {
      access_token: accessToken,
      household_id: householdId,
      refresh_token: refreshToken ?? null,
    },
  });

  if (error) {
    // functions.invoke hides non-2xx bodies in error.context, not data.
    const body = await parseInvokeErrorBody(error);
    if (body && body.error === 'UNSUPPORTED_ACCOUNT') {
      const reason =
        body.reason === 'missing_scopes'
          ? 'missing_scopes'
          : 'non_personal_domain';
      throw new UnsupportedAccountError(
        reason,
        typeof body.email === 'string' ? body.email : undefined,
      );
    }
    if (body && body.error === 'ACCOUNT_MISMATCH') {
      throw new AccountMismatchError(
        typeof body.signed_in_email === 'string'
          ? body.signed_in_email
          : undefined,
        typeof body.selected_email === 'string'
          ? body.selected_email
          : undefined,
      );
    }
    // Anything else the function rejected with is a server verdict, not a
    // connectivity problem. Carry it through as a typed error so the UI stops
    // blaming the user's internet for a 403.
    if (body && typeof body.error === 'string') {
      throw new ForwardingSetupError(setupErrorCodeOf(body), body.error);
    }
    throw error;
  }
  return data as GmailForwardingResult;
}

/**
 * Rebuild Gmail filters from the household's current `approved_senders`,
 * using the household's stored refresh_token (server-side mint).
 *
 * Unlike `setupGmailForwarding`, this does NOT require a fresh
 * `provider_token` in the client session — so it works after the ~1h
 * OAuth window where session.provider_token has been dropped by
 * Supabase. Use this from Settings → Approved Senders after every
 * add / remove so the user's inbox reflects the current list.
 *
 * Returns `{ ok: false, needs_oauth: true }` when there's no
 * subscription or the stored refresh_token has been revoked; the caller
 * should surface a "Reconnect Gmail" prompt in that case.
 */
export async function resyncGmailFilters(
  householdId: string,
): Promise<GmailFiltersResyncResult> {
  const { data, error } = await invokeFunction('gmail-filters-resync', {
    body: { household_id: householdId },
  });
  if (error) throw error;
  return data as GmailFiltersResyncResult;
}
